import React from 'react';
import PropTypes from 'prop-types';
import { Text } from '@reservamos/elements';
import { useTranslation } from 'react-i18next';
import LocalizedCurrency from 'components/LocalizedCurrency';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';

/**
 * PreviousPrice component.
 * Render the previous price with brand-specific label using i18n context.
 *
 * @param {object} props - Component props.
 * @param {string} props.price - The previous price.
 * @param {boolean} props.italic - Whether the previous price label should be italic.
 * @param {boolean} props.lineThrough - Whether the previous price amount should be crossed out.
 * @returns {JSX.Element} The rendered component.
 */
const PreviousPrice = ({ price, italic, lineThrough }) => {
  const { t } = useTranslation('search');
  const { brand } = useWhitelabelEnvs();

  // Coerce string price to number with validation
  const numericPrice = parseFloat(price);
  const validPrice = Number.isNaN(numericPrice) ? 0 : numericPrice;
  const displayLabel = t('label.previous_price', { context: brand });

  return (
    <Text color="grayLight" size="S" italic={italic} elementType="span">
      {`${displayLabel}: `}
      <LocalizedCurrency
        color="grayLight"
        decimals={0}
        price={validPrice}
        size="S"
        currencyStyle="currency"
        lineThrough={lineThrough}
      />
    </Text>
  );
};

PreviousPrice.propTypes = {
  /** The previous price to display */
  price: PropTypes.string.isRequired,
  italic: PropTypes.bool,
  lineThrough: PropTypes.bool,
};

PreviousPrice.defaultProps = {
  italic: true,
  lineThrough: true,
};

export default PreviousPrice;
